import { EventType } from 'src/components/Chat/Chat.enum';
import type { tChatEvent } from 'src/components/Chat/Chat.types';
import {
  CLOSE_ARCHIVE_DEFAULT,
  CLOSE_ERROR_REASONS,
  CLOSE_MAX_INTEGER_DIGITS,
  CLOSE_MAX_NOTE,
  CLOSE_MAX_OTHER_TEXT,
  CLOSE_MAX_WHAT_SOLD,
} from './LeadClose.constants';
import type { tCloseLeadRef } from './LeadClose.context';
import type {
  tCloseEditBody,
  tCloseResult,
  tCloseRegisterBody,
  tCloseSettings,
  tCloseSource,
  tCloseSummary,
  tCloseType,
  tPriorHandling,
} from './LeadClose.types';

/** The option value of "Otro": a reason typed by the seller instead of a listed one. */
export const CLOSE_OTHER_OPTION = 'other';

export type tCloseForm = {
  type: tCloseType;
  amount: string;
  currency: string;
  saleDate: string;
  whatSold: string;
  optionUuid: string | null;
  otherText: string;
  note: string;
  priorHandling: tPriorHandling;
  archive: boolean;
};

export type tAmountError =
  | 'amount_required'
  | 'amount_not_positive'
  | 'amount_decimals'
  | 'amount_too_large'
  | 'amount_invalid';

/**
 * Checks a typed amount the way leads-service's Money does (KTD25): digits
 * with an optional point, at most 12 integer digits, no more decimals than the
 * currency, greater than zero. Nothing is rounded.
 */
export function amountError(
  raw: string,
  decimals: number,
): tAmountError | null {
  const value = raw.trim();
  if (value === '') return 'amount_required';
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return 'amount_invalid';
  const [, integer, fraction = ''] = match;
  if (integer.replace(/^0+(?=\d)/, '').length > CLOSE_MAX_INTEGER_DIGITS)
    return 'amount_too_large';
  if (fraction.length > decimals) return 'amount_decimals';
  if (Number(value) === 0) return 'amount_not_positive';
  return null;
}

export function currencyDecimals(
  settings: tCloseSettings,
  code: string,
): number {
  return (
    settings.currencies.find((currency) => currency.code === code)?.decimals ??
    2
  );
}

/** A sale that is still live: the next one is "Registrar otra venta" (R5). */
export function hasLiveSale(summary: tCloseSummary | null): boolean {
  return Boolean(
    summary?.results.some(
      (result) => result.type === 'sold' && result.deleted_at === null,
    ),
  );
}

export function initialCloseForm(args: {
  type: tCloseType;
  settings: tCloseSettings;
  summary: tCloseSummary;
  leadValue: number | null;
  today: string;
}): tCloseForm {
  const { type, settings, summary, leadValue, today } = args;
  const firstSale = type === 'sold' && !hasLiveSale(summary);
  return {
    type,
    amount: firstSale && leadValue && leadValue > 0 ? String(leadValue) : '',
    currency: settings.sales_currency,
    saleDate: today,
    whatSold: '',
    optionUuid: null,
    otherText: '',
    note: '',
    priorHandling: 'none',
    archive: CLOSE_ARCHIVE_DEFAULT[type],
  };
}

/** Reason codes per field; an empty object means the form can be sent. */
export function validateCloseForm(
  form: tCloseForm,
  context: { settings: tCloseSettings; summary: tCloseSummary },
): Partial<Record<keyof tCloseForm, string>> {
  const errors: Partial<Record<keyof tCloseForm, string>> = {};
  if (form.type === 'sold') {
    const amount = amountError(
      form.amount,
      currencyDecimals(context.settings, form.currency),
    );
    if (amount) errors.amount = amount;
    if (form.whatSold.trim().length > CLOSE_MAX_WHAT_SOLD)
      errors.whatSold = 'what_sold_too_long';
    if (context.summary.prior_amount && form.priorHandling === 'none')
      errors.priorHandling = 'prior_choice_required';
  } else if (form.optionUuid === null) {
    errors.optionUuid = 'reason_required';
  } else if (form.optionUuid === CLOSE_OTHER_OPTION) {
    const text = form.otherText.trim();
    if (text === '') errors.otherText = 'reason_required';
    else if (text.length > CLOSE_MAX_OTHER_TEXT)
      errors.otherText = 'other_text_too_long';
  }
  if (form.note.trim().length > CLOSE_MAX_NOTE) errors.note = 'note_too_long';
  return errors;
}

function optional(text: string): string | undefined {
  const value = text.trim();
  return value === '' ? undefined : value;
}

export function toRegisterBody(
  form: tCloseForm,
  args: { summary: tCloseSummary; source: tCloseSource },
): tCloseRegisterBody {
  const common = {
    note: optional(form.note),
    source: args.source,
    archive: form.archive,
  };
  if (form.type === 'sold') {
    return {
      type: 'sold',
      amount: form.amount.trim(),
      currency: form.currency,
      sale_date: form.saleDate,
      what_sold: optional(form.whatSold),
      prior_handling: args.summary.prior_amount ? form.priorHandling : 'none',
      prior_amount: args.summary.prior_amount,
      ...common,
    };
  }
  const other = form.optionUuid === CLOSE_OTHER_OPTION;
  return {
    type: form.type,
    option_uuid: other ? null : form.optionUuid,
    ...(other && { other_text: form.otherText.trim() }),
    ...common,
  };
}

export type tCloseMenuEntry = {
  label: 'sold' | 'another_sale' | 'lost' | 'resolved';
  type: tCloseType;
};

/** "Marcar resultado": "Registrar otra venta" first while a sale is live, "Vendido" otherwise (U13). */
export function closeMenuEntries(
  summary: tCloseSummary | null,
): tCloseMenuEntry[] {
  return [
    hasLiveSale(summary)
      ? { label: 'another_sale', type: 'sold' }
      : { label: 'sold', type: 'sold' },
    { label: 'lost', type: 'lost' },
    { label: 'resolved', type: 'resolved' },
  ];
}

/** The refusal's reason: leads-service and bff-web both put it at data.code. */
export function closeErrorReason(error: unknown): string | null {
  const code = (
    error as { response?: { data?: { data?: { code?: unknown } } } } | null
  )?.response?.data?.data?.code;
  return typeof code === 'string' ? code : null;
}

/** The copy key of a reason; "choose a reason" names the result type. */
export function closeErrorCopyKey(reason: string, type: tCloseType): string {
  if (reason === 'reason_required')
    return `leadClose.errors.reason_required.${type}`;
  const known = (CLOSE_ERROR_REASONS as readonly string[]).includes(reason);
  return `leadClose.errors.${known ? reason : 'unavailable'}`;
}

/**
 * The typed adapter from a lead as the chat or the board holds it to what the
 * form needs (`.claude/rules/modals.md`). Lead payloads carry the value as a
 * number or as a decimal string ("890.00"); zero means no value to prefill.
 */
export function toCloseLeadRef(lead: {
  id: string;
  name: string;
  stage?: { name?: string } | null;
  value?: number | string | null;
}): tCloseLeadRef {
  const value = Number(lead.value ?? 0);
  return {
    id: lead.id,
    name: lead.name,
    stageName: lead.stage?.name ?? null,
    value: Number.isFinite(value) && value > 0 ? value : null,
  };
}

/** An edit replaces the result's fields (leads-service requires them all again) at the revision it read. */
export function toEditBody(
  form: tCloseForm,
  args: { summary: tCloseSummary; revision: number },
): tCloseEditBody {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { source, archive, ...fields } = toRegisterBody(form, {
    summary: args.summary,
    source: 'chat_bar',
  });
  return { ...fields, revision: args.revision };
}

const MONTHS = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

/** "$890", "$1,500.5", or "$890 USD" when the code must show (more than one currency). */
export function formatMoney(
  amount: string | number,
  currency?: string | null,
  withCode = false,
): string {
  const text = `$${Number(amount).toLocaleString('es-MX', { maximumFractionDigits: 2 })}`;
  return withCode && currency ? `${text} ${currency}` : text;
}

export function firstName(name: string): string {
  return (
    name
      .replace(/^Sr\.\s+/, '')
      .trim()
      .split(/\s+/)[0] ?? ''
  );
}

/** "7 oct" from "2026-10-07"; empty when there is no date. */
export function saleDateText(iso: string | null | undefined): string {
  const [, month, day] = (iso ?? '').split('-');
  return month && day ? `${Number(day)} ${MONTHS[Number(month) - 1]}` : '';
}

/** An edit starts from the result as it was saved (amount as typed, without trailing zeros). */
export function formFromResult(result: tCloseResult): tCloseForm {
  return {
    type: result.type,
    amount: result.amount === null ? '' : String(Number(result.amount)),
    currency: result.currency ?? '',
    saleDate: result.sale_date ?? '',
    whatSold: result.what_sold ?? '',
    optionUuid:
      result.option?.uuid ?? (result.other_text ? CLOSE_OTHER_OPTION : null),
    otherText: result.other_text ?? '',
    note: result.note ?? '',
    priorHandling: result.prior_handling,
    archive: false,
  };
}

/** "2026-10-20 17:03:11" (UTC, as leads-service writes it) as an ISO instant the chat orders by. */
function utcInstant(stamp: string): string {
  return stamp.includes('T') ? stamp : `${stamp.replace(' ', 'T')}Z`;
}

/**
 * The lead's live results as system events of its conversation (U14), only
 * inside the range of messages already loaded, so a result never jumps when
 * older messages load. `to` is null when the window reaches the newest message.
 */
export function leadCloseChatEvents(
  results: tCloseResult[],
  range: { from: string | null; to: string | null },
  chatID: string,
): tChatEvent[] {
  const from = range.from ? Date.parse(range.from) : -Infinity;
  const to = range.to ? Date.parse(range.to) : Infinity;
  return results
    .filter((result) => result.deleted_at === null)
    .map((result) => ({ result, createdAt: utcInstant(result.created_at) }))
    .filter(({ createdAt }) => {
      const at = Date.parse(createdAt);
      return at >= from && at <= to;
    })
    .map(({ result, createdAt }) => ({
      id: `lead-close-${result.uuid}`,
      chatID,
      agendaEvent: null,
      content: null,
      createdAt,
      createdBy: result.actor_uuid ?? '',
      deletedAt: '',
      followUp: null,
      message: null,
      note: null,
      type: EventType.SYSTEM,
      updatedAt: createdAt,
      system: { subtype: 'lead-close', leadClose: result },
    }));
}

const CLOSE_TYPES = new Set<string>(['sold', 'lost', 'resolved']);

/** The result a mapped stage asks for (`prompt.open_form` beside the lead update's answer, U11). */
export function promptOf(answer: unknown): tCloseType | null {
  const type = (answer as { prompt?: { open_form?: unknown } } | null)?.prompt
    ?.open_form;
  return typeof type === 'string' && CLOSE_TYPES.has(type)
    ? (type as tCloseType)
    : null;
}
